"use client";

import * as React from "react";
import { useMemo } from "react";
import Image from "next/image";
import * as AvatarPrimitive from "@radix-ui/react-avatar";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";
import { getInitials } from "@/lib/utils/get-initials";

type AvatarSize = "xs" | "sm" | "md" | "lg" | "xl" | "2xl" | "full";

const AVATAR_SIZES: Record<AvatarSize, string> = {
  xs: "24px",
  sm: "32px",
  md: "40px",
  lg: "64px",
  xl: "(min-width: 768px) 160px, 128px",
  "2xl": "144px",
  full: "128px",
};

function isSvgUrl(url: string) {
  return url.includes(".svg") || url.includes("/svg");
}

const avatarVariants = cva(
  "relative inline-flex shrink-0 overflow-hidden",
  {
    variants: {
      size: {
        xs: "size-6",
        sm: "size-8",
        md: "size-10",
        lg: "size-16",
        xl: "size-32 md:size-40",
        "2xl": "size-36",
        full: "size-full",
      },
      shape: {
        circle: "rounded-full",
        square: "rounded-none",
        rounded: "rounded-lg",
      },
    },
    defaultVariants: {
      size: "sm",
      shape: "circle",
    },
  }
);

export interface AvatarProps
  extends React.ComponentPropsWithoutRef<typeof AvatarPrimitive.Root>,
    VariantProps<typeof avatarVariants> {
  src?: string | null;
  alt?: string;
  name?: string;
  fallback?: React.ReactNode;
  sizes?: string;
  imageClassName?: string;
  fallbackClassName?: string;
}

export function Avatar({
  src,
  alt,
  name,
  fallback,
  size,
  shape,
  sizes: sizesProp,
  imageClassName,
  fallbackClassName,
  className,
  ...props
}: AvatarProps) {
  const resolvedAlt = alt ?? name;
  const resolvedFallback = useMemo(() => fallback ?? getInitials(name), [fallback, name]);
  const resolvedSizes = sizesProp ?? AVATAR_SIZES[(size ?? "sm") as AvatarSize];

  return (
    <AvatarPrimitive.Root
      role="img"
      aria-label={resolvedAlt}
      className={cn(avatarVariants({ size, shape }), className)}
      {...props}
    >
      {src && (
        isSvgUrl(src) ? (
          <AvatarPrimitive.Image
            src={src}
            alt=""
            className={cn("aspect-square size-full object-cover", imageClassName)}
          />
        ) : (
          <Image
            src={src}
            alt=""
            fill
            sizes={resolvedSizes}
            className={cn("object-cover", imageClassName)}
          />
        )
      )}
      <span
        aria-hidden="true"
        className={cn(
          "flex size-full items-center justify-center bg-muted rounded-[inherit]",
          fallbackClassName
        )}
      >
        {resolvedFallback}
      </span>
    </AvatarPrimitive.Root>
  );
}
